// Constants shared across the Campaign Forge V2 wizard. These are the
// contract the steps + adapter both read from.
import { SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS } from '../../../../../campaignFeatureAccess';

export const TOTAL_STEPS = 3;

export const FEATURES = SELECTABLE_CAMPAIGN_FEATURE_DEFINITIONS;

export const MAX_TAGLINE_LENGTH = 120;

export const TEST_CAMPAIGN_CHARACTERS = [
  { presetKey: 'lika', name: "Li'ka", race: 'Half-Elf', charClass: 'Cleric', maxHp: 42 },
  { presetKey: 'ventyl', name: 'Ventyl', race: 'Half-Elf', charClass: 'Ranger', maxHp: 42 },
  { presetKey: 'eve', name: 'Eve', race: 'Human', charClass: 'Ranger', maxHp: 38 },
  { presetKey: 'khan', name: 'Khan', race: 'Tiefling', charClass: 'Rogue', maxHp: 32 },
];

export const TEST_PRESET_CATEGORY_OPTIONS = [
  { key: 'systems', label: 'Systems' },
  { key: 'items', label: 'Items' },
  { key: 'crafting', label: 'Crafting' },
  { key: 'professions', label: 'Professions' },
  { key: 'buffsDebuffs', label: 'Buffs/debuffs' },
  { key: 'combat', label: 'Combat' },
];

export const DEFAULT_TEST_PRESET_CATEGORIES = Object.fromEntries(
  TEST_PRESET_CATEGORY_OPTIONS.map((option) => [option.key, true]),
);

export const TEST_PRESET_CATEGORY_FEATURE_GATES = Object.freeze({
  systems: ['systems'],
  crafting: ['crafting'],
  professions: ['crafting', 'professions'],
  combat: ['combat'],
});

export const LINK_OPTIONS = [
  { key: 'npc',         label: 'NPC Library',                desc: 'Use NPC definitions; campaign state stays local' },
  { key: 'items',       label: 'Item Library',               desc: 'Share item definitions' },
  { key: 'crafting',    label: 'Crafting rules + recipes',   desc: 'Share crafting system' },
  { key: 'professions', label: 'Profession rules + recipes', desc: 'Share profession system' },
  { key: 'shops',       label: 'Shops',                      desc: 'Share merchant and listing templates' },
  { key: 'systems',     label: 'Systems',                    desc: 'Share reusable system templates' },
];

export const HOMEBREW_GROUPS = [
  {
    key: 'survival',
    label: 'Survival settings',
    items: [
      { key: 'durability',       label: 'Durability' },
      { key: 'foodSpoilage',     label: 'Food Spoilage' },
      { key: 'hungerStarvation', label: 'Hunger / Starvation' },
      { key: 'diets',            label: 'Diets' },
      { key: 'diseases',         label: 'Diseases' },
    ],
  },
  {
    key: 'resources',
    label: 'Resources',
    items: [
      { key: 'mana', label: 'Mana' },
    ],
  },
];

export const ALL_HOMEBREW_KEYS = HOMEBREW_GROUPS.flatMap((g) => g.items.map((item) => item.key));

// Picking a template applies these toggles on top of all-false homebrew.
export const TEMPLATE_DEFAULTS = {
  standard: {},
  survival: { durability: true, foodSpoilage: true, hungerStarvation: true, diseases: true },
};

export const DAYS = [
  { key: 'mon', label: 'M', short: 'Mon', full: 'Monday' },
  { key: 'tue', label: 'T', short: 'Tue', full: 'Tuesday' },
  { key: 'wed', label: 'W', short: 'Wed', full: 'Wednesday' },
  { key: 'thu', label: 'T', short: 'Thu', full: 'Thursday' },
  { key: 'fri', label: 'F', short: 'Fri', full: 'Friday' },
  { key: 'sat', label: 'S', short: 'Sat', full: 'Saturday' },
  { key: 'sun', label: 'S', short: 'Sun', full: 'Sunday' },
];

// Theme variant overrides (per-theme accent palettes) for the forge picker.
// Independent of the dashboard's THEME_VARIATIONS table — these are a
// simpler 2-color override (accent + accentLight) that the picker swatches
// against. Persisted to the campaign as `baseVariant` (variant id).
// Hex values match prototypes/campaign-creation-prototype.jsx lines
// 191-222 verbatim so the live wizard reads as the same artifact.
export const THEME_VARIANTS = {
  'fantasy-dark': [
    { id: 'amethyst', name: 'Amethyst', accent: '#9b59b6', accentLight: '#d4a5ff' },
    { id: 'rose',     name: 'Rose',     accent: '#c2649a', accentLight: '#f0a8c8' },
    { id: 'indigo',   name: 'Indigo',   accent: '#6c5dd3', accentLight: '#a99bf0' },
  ],
  'royal-court': [
    { id: 'gold',    name: 'Gold',    accent: '#d4af37', accentLight: '#f1d97e' },
    { id: 'crimson', name: 'Crimson', accent: '#c34b3f', accentLight: '#e8857c' },
    { id: 'bronze',  name: 'Bronze',  accent: '#a87642', accentLight: '#dcaa78' },
  ],
  'verdant-grove': [
    { id: 'leaf',    name: 'Leaf',    accent: '#5ea876', accentLight: '#a8e3bd' },
    { id: 'moss',    name: 'Moss',    accent: '#7a9550', accentLight: '#c3d49a' },
    { id: 'sage',    name: 'Sage',    accent: '#88a890', accentLight: '#c4d6cb' },
  ],
  'classic-fantasy': [
    { id: 'amber',   name: 'Amber',   accent: '#dc9c50', accentLight: '#f4c98a' },
    { id: 'oxblood', name: 'Oxblood', accent: '#a8533e', accentLight: '#d68d75' },
    { id: 'wheat',   name: 'Wheat',   accent: '#c9a96a', accentLight: '#e6cda0' },
  ],
  'ocean-depths': [
    { id: 'tide',    name: 'Tide',    accent: '#64aadc', accentLight: '#a8d6f5' },
    { id: 'kelp',    name: 'Kelp',    accent: '#4f9c8a', accentLight: '#88c8b8' },
    { id: 'lantern', name: 'Lantern', accent: '#7bc4d6', accentLight: '#b5e2ed' },
  ],
  'ember-frost': [
    { id: 'ember',   name: 'Ember',   accent: '#e67850', accentLight: '#f5b095' },
    { id: 'frost',   name: 'Frost',   accent: '#7aa8d6', accentLight: '#b8d4ed' },
    { id: 'coal',    name: 'Coal',    accent: '#8c7088', accentLight: '#b8a6b5' },
  ],
};
